import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiPhone, FiMapPin, FiHeart, FiExternalLink } = FiIcons;

const Footer = () => {
  const quickLinks = [
    { name: 'How It Works', id: 'how-it-works' },
    { name: 'About', id: 'about' },
    { name: 'FAQ', id: 'faq' },
    { name: 'Contact', id: 'contact' }
  ];

  const legalLinks = [
    { name: 'Terms of Service', href: '#terms' },
    { name: 'Privacy Policy', href: '#privacy' },
    { name: 'Accessibility', href: '#accessibility' }
  ];

  const socialLinks = [
    { 
      name: 'LinkedIn', 
      href: 'https://www.linkedin.com/in/sanna-darwish-m-s-ccc-slp-b2a3491a8',
      icon: 'FiLinkedin'
    },
    { 
      name: 'Facebook', 
      href: 'https://Facebook.com/337098602826472',
      icon: 'FiFacebook'
    },
    { 
      name: 'Instagram', 
      href: 'https://www.instagram.com/wethebillions',
      icon: 'FiInstagram'
    },
    { 
      name: 'WhatsApp', 
      href: 'https://wa.me/+12026560054',
      icon: 'FiMessageCircle'
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-16">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-3 mb-6">
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753416293845-4.png"
                alt="We The Billions Brain Icon"
                className="w-10 h-10"
              />
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753170072528-v%20lg%20We%20The%20Billions%20logo.png"
                alt="We The Billions"
                className="h-8 w-auto brightness-0 invert"
              />
            </div>
            <p className="text-gray-400 mb-4 text-sm leading-relaxed">
              86 billion neurons. 1 unique YOU.
            </p>
            <p className="text-gray-400 text-sm mb-4">
              Virtual Sessions (Globally) & In-Person Sessions (MD & VA)
            </p>
            <p className="text-gray-400 text-sm flex items-center gap-2">
              Made with <SafeIcon icon={FiHeart} className="w-4 h-4 text-red-500" /> for every unique brain
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
              <li>
                <a
                  href="https://www.wethebillions.com/quiz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-white transition-colors duration-200 text-sm flex items-center gap-1"
                >
                  Take Quiz <SafeIcon icon={FiExternalLink} className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-6">Contact</h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiMail} className="w-4 h-4 text-accent-gold" />
                <a 
                  href="mailto:hello@wethebillions.com"
                  className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                >
                  hello@wethebillions.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiPhone} className="w-4 h-4 text-accent-gold" />
                <a 
                  href="tel:+12026560054"
                  className="text-gray-400 hover:text-white transition-colors duration-200 text-sm"
                >
                  (202) 656-0054
                </a>
              </div>
              <div className="flex items-center gap-3">
                <SafeIcon icon={FiMapPin} className="w-4 h-4 text-accent-gold" />
                <span className="text-gray-400 text-sm">Global & MD/VA</span>
              </div>
              <div className="pt-2">
                <a
                  href="https://www.wethebillions.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent-gold hover:text-accent-yellow transition-colors duration-200 text-sm"
                >
                  www.wethebillions.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Social & Legal */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-6">Follow Us</h3>
            <div className="flex gap-4 mb-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-accent-magenta transition-colors duration-200"
                  aria-label={social.name}
                >
                  <SafeIcon icon={FiIcons[social.icon]} className="w-5 h-5" />
                </a>
              ))}
            </div>
            
            <div className="space-y-2">
              {legalLinks.map((link) => (
                <div key={link.name}>
                  <a
                    href={link.href}
                    className="text-gray-400 hover:text-white transition-colors duration-200 text-sm block"
                  >
                    {link.name}
                  </a>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="border-t border-gray-800 pt-8 text-center"
        >
          <p className="text-gray-400 text-sm">
            © 2024 We The Billions. All rights reserved. | Executive Function Therapy for Recovery
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;